/**
 * Situation reçue du back-office tiers (arbitrage du 03/09/2026).
 *
 * Depuis la bascule, l'application ne tient plus les positions ni le cash :
 * elle les reçoit. Le fichier porte trois blocs — les soldes espèces, les
 * positions titres, et le journal des mouvements de la période.
 *
 * Ce module est partagé de propos délibéré : le parseur du navigateur, la
 * validation de l'API et le jeu de démonstration passent tous par lui. Une
 * dérive du format casse alors `db:historique`, ce qui est exactement le
 * signal recherché.
 */

/** Blocs du fichier, dans l'ordre où ils doivent y figurer. */
export type BlocSituation = 'SOLDES' | 'POSITIONS' | 'MOUVEMENTS';

export interface LigneSolde {
  referenceMandat: string;
  dateSituation: string;
  soldeEspece: string;
  ligneSource: number;
}

export interface LignePosition {
  referenceMandat: string;
  dateSituation: string;
  type: string;
  isin?: string;
  code?: string;
  libelle?: string;
  quantite: string;
  pru: string;
  montantInvesti?: string;
  /** Requis pour une ligne BDT absente du référentiel (RG-17). */
  dateEmission?: string;
  dateEcheance?: string;
  tauxFacial?: string;
  nominalUnitaire?: string;
  ligneSource: number;
}

export interface LigneMouvement {
  referenceMandat: string;
  referenceMouvement: string;
  dateOperation?: string;
  dateValeur: string;
  type: string;
  sens: string;
  montant: string;
  isin?: string;
  quantite?: string;
  libelle?: string;
  sequence?: string;
  /**
   * Référence de l'ordre GSM (`ORD-…`) que le mouvement exécute, reprise du
   * fichier exporté du panier. Facultative : quand le tiers la renseigne, le
   * réalisé de l'ordre se lit directement au lieu d'être déduit des positions.
   */
  referenceOrdre?: string;
  ligneSource: number;
}

export interface SituationAnalysee {
  dateSituation: string | null;
  soldes: LigneSolde[];
  positions: LignePosition[];
  mouvements: LigneMouvement[];
  /** Ce qui empêche l'envoi : colonne obligatoire absente, bloc manquant. */
  manques: string[];
}

/* ── Colonnes attendues, par bloc ─────────────────────────────────────── */

const COLONNES: Record<BlocSituation, Record<string, string>> = {
  SOLDES: {
    reference_mandat: 'referenceMandat',
    date_situation: 'dateSituation',
    solde_espece: 'soldeEspece',
  },
  POSITIONS: {
    reference_mandat: 'referenceMandat',
    date_situation: 'dateSituation',
    type: 'type',
    isin: 'isin',
    code: 'code',
    libelle: 'libelle',
    quantite: 'quantite',
    pru: 'pru',
    montant_investi: 'montantInvesti',
    date_emission: 'dateEmission',
    date_echeance: 'dateEcheance',
    taux_facial: 'tauxFacial',
    nominal_unitaire: 'nominalUnitaire',
  },
  MOUVEMENTS: {
    reference_mandat: 'referenceMandat',
    reference_mouvement: 'referenceMouvement',
    date_operation: 'dateOperation',
    date_valeur: 'dateValeur',
    type: 'type',
    sens: 'sens',
    montant: 'montant',
    isin: 'isin',
    quantite: 'quantite',
    libelle: 'libelle',
    sequence: 'sequence',
    reference_ordre: 'referenceOrdre',
  },
};

const OBLIGATOIRES: Record<BlocSituation, string[]> = {
  SOLDES: ['reference_mandat', 'date_situation', 'solde_espece'],
  POSITIONS: ['reference_mandat', 'date_situation', 'type', 'quantite', 'pru'],
  MOUVEMENTS: ['reference_mandat', 'reference_mouvement', 'date_valeur', 'type', 'sens', 'montant'],
};

/** Marqueur de bloc dans le fichier : `#BLOC SOLDES`, etc. */
const MARQUEUR = /^#\s*BLOC\s+(SOLDES|POSITIONS|MOUVEMENTS)\s*$/i;

/**
 * Analyse du fichier de situation.
 *
 * Conventions de la maison (doc 03, § 5) : `;` en séparateur, décimale `.`,
 * dates `AAAA-MM-JJ`, en-tête par bloc, lecture **par nom de colonne** et non
 * par position — un partenaire qui réordonne ses colonnes ne doit rien casser.
 */
export function analyserSituation(contenu: string): SituationAnalysee {
  const lignes = contenu
    .replace(/^﻿/, '')
    .split(/\r?\n/)
    .map((texte, index) => ({ texte: texte.trim(), numero: index + 1 }))
    .filter((l) => l.texte.length > 0);

  const resultat: SituationAnalysee = {
    dateSituation: null,
    soldes: [],
    positions: [],
    mouvements: [],
    manques: [],
  };

  let bloc: BlocSituation | null = null;
  let entetes: string[] = [];

  for (const ligne of lignes) {
    const marqueur = MARQUEUR.exec(ligne.texte);
    if (marqueur) {
      bloc = marqueur[1]!.toUpperCase() as BlocSituation;
      entetes = [];
      continue;
    }
    // Tout autre commentaire est ignoré.
    if (ligne.texte.startsWith('#')) continue;
    if (!bloc) {
      resultat.manques.push(
        `Ligne ${ligne.numero} : aucun bloc déclaré. Le fichier doit commencer par « #BLOC SOLDES ».`,
      );
      break;
    }

    const cellules = ligne.texte.split(';').map((c) => c.trim());

    if (entetes.length === 0) {
      entetes = cellules.map((c) => c.toLowerCase());
      const attendues = COLONNES[bloc];
      for (const obligatoire of OBLIGATOIRES[bloc]) {
        if (!entetes.includes(obligatoire)) {
          resultat.manques.push(`Bloc ${bloc} : la colonne « ${obligatoire} » est absente.`);
        }
      }
      const inconnues = entetes.filter((e) => e && !attendues[e]);
      if (inconnues.length > 0) {
        resultat.manques.push(`Bloc ${bloc} : colonnes ignorées — ${inconnues.join(', ')}.`);
      }
      continue;
    }

    const champs = COLONNES[bloc];
    const objet: Record<string, string | number> = { ligneSource: ligne.numero };
    entetes.forEach((entete, i) => {
      const champ = champs[entete];
      const valeur = cellules[i];
      // Une cellule vide n'est pas transmise : l'API traite l'absence, pas le vide.
      if (champ && valeur) objet[champ] = valeur;
    });

    if (bloc === 'SOLDES') resultat.soldes.push(objet as unknown as LigneSolde);
    else if (bloc === 'POSITIONS') resultat.positions.push(objet as unknown as LignePosition);
    else resultat.mouvements.push(objet as unknown as LigneMouvement);
  }

  const dates = new Set(
    [
      ...resultat.soldes.map((l) => l.dateSituation),
      ...resultat.positions.map((l) => l.dateSituation),
    ].filter(Boolean),
  );
  if (dates.size > 1) {
    resultat.manques.push(
      `Le fichier porte plusieurs dates de situation (${[...dates].join(', ')}) : il en faut une seule.`,
    );
  }
  resultat.dateSituation = [...dates][0] ?? null;

  if (resultat.soldes.length === 0) {
    resultat.manques.push('Le bloc SOLDES est vide : chaque mandat doit y figurer.');
  }

  return resultat;
}

/**
 * Types de mouvement admis, volontairement **exhaustifs et sans repli**.
 *
 * Un coupon pris pour un versement ferait figurer au relevé un apport que le
 * client n'a jamais fait ; un versement pris pour une régularisation
 * effacerait le sien de la collecte ; un retrait mal typé laisserait une
 * demande de sortie ouverte, ou en solderait une à tort.
 * Rien en aval ne rattrape l'erreur, qui ne se lit que sur un relevé, des
 * mois plus tard.
 *
 * Un type absent de cette liste rejette la situation du mandat.
 */
export const TYPES_MOUVEMENT_ADMIS = [
  'VERSEMENT',
  'RETRAIT',
  'ACHAT_TITRE',
  'VENTE_TITRE',
  'DIVIDENDE',
  'COUPON',
  'REMBOURSEMENT',
  'DISTRIBUTION_OPCVM',
  'FRAIS_GESTION',
  'FRAIS_COURTAGE',
  'REGULARISATION',
] as const;

export type TypeMouvementAdmis = (typeof TYPES_MOUVEMENT_ADMIS)[number];

export function typeMouvementAdmis(valeur: string): valeur is TypeMouvementAdmis {
  return (TYPES_MOUVEMENT_ADMIS as readonly string[]).includes(valeur);
}

/** Types d'instrument admis dans le bloc des positions. */
export const TYPES_POSITION_ADMIS = ['ACTION', 'BDT', 'OPCVM'] as const;

/** Flux client, les seuls qui font varier le nombre de parts (doc 01, § 8.3). */
export const TYPES_FLUX_CLIENT = ['VERSEMENT', 'RETRAIT'] as const;
